import { useMemo, useRef } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ChartSpec } from '../../types/api';
import { exportChartAsPNG, exportChartAsPDF } from '../../utils/chartExport';

const PALETTE = ['#1e40af', '#0ea5e9', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0d9488', '#db2777'];

// =============================================================================
// Formatting helpers
// =============================================================================

/**
 * Format a number as USD with compact notation. Currency-style by default;
 * pass `style: 'plain'` to drop the "$" prefix (useful for axis ticks where
 * the axis label already says "$").
 *
 * Examples:
 *   1_653_921  → "$1.65M"
 *   895_321    → "$895K"
 *   7_300      → "$7.3K"
 *   456        → "$456"
 *   12.5       → "$13" (rounded)
 */
function fmtMoney(n: number, style: 'currency' | 'plain' = 'currency'): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const prefix = style === 'currency' ? '$' : '';
  if (abs >= 1_000_000) return `${sign}${prefix}${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (abs >= 1_000) return `${sign}${prefix}${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return `${sign}${prefix}${Math.round(n).toLocaleString('en-US')}`;
}

function fmtPercent(value: number, total: number): string {
  if (!total || total === 0) return '0%';
  const pct = (value / total) * 100;
  return pct >= 10 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
}

// =============================================================================
// Custom Tooltip — consistent formatting + cleaner layout than Recharts default
// =============================================================================

interface TooltipPayload {
  name: string;
  value: number;
  color?: string;
  payload?: Record<string, unknown>;
}
interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string | number;
  total?: number; // for pie — show percent of whole
}

function CustomTooltip({ active, payload, label, total }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      {label !== undefined && (
        <div className="mb-1 font-semibold text-slate-800">{String(label)}</div>
      )}
      <ul className="space-y-0.5">
        {payload.map((p, i) => (
          <li key={i} className="flex items-center gap-2 tabular-nums">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: p.color }}
              aria-hidden="true"
            />
            <span className="text-slate-700">{p.name}:</span>
            <span className="font-semibold text-slate-900">{fmtMoney(p.value)}</span>
            {total !== undefined && (
              <span className="text-slate-500">({fmtPercent(p.value, total)})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// Custom Legend — colored dot + name + value + (optional) percent
// More informative than Recharts' default which only shows dot + name.
// =============================================================================

interface LegendEntry {
  name: string;
  color: string;
  value: number;
}

function RichLegend({
  entries,
  total,
  showPercent,
  orientation = 'horizontal',
}: {
  entries: LegendEntry[];
  total?: number;
  showPercent?: boolean;
  /**
   * - `horizontal`: bottom-of-chart, wraps across multiple rows. Used on
   *   mobile and as a fallback when the chart is too narrow for a side
   *   legend.
   * - `vertical`: side-of-chart (one entry per line). Cleaner read for the
   *   diligence audience — matches the pattern in their existing dashboards
   *   (Power BI / Looker conventions).
   */
  orientation?: 'horizontal' | 'vertical';
}) {
  // Sort descending by value so the biggest series shows first.
  const sorted = [...entries].sort((a, b) => b.value - a.value);

  const containerClass =
    orientation === 'vertical'
      ? 'flex flex-col gap-1.5 text-xs'
      : 'mx-auto mt-2 flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 text-xs';

  // Each legend row gets an EXPLICIT line-height (`leading-[24px]`) and a
  // hard min-height. This is the html2canvas-safe pattern for text in flex
  // rows: tight implicit line-boxes were clipping the tops of capital
  // letters in PDF/PNG exports. Now there's plenty of vertical room above
  // the cap-line of each character.
  const itemClass = 'flex min-h-[24px] items-center gap-2 leading-[20px] tabular-nums';

  return (
    <ul className={containerClass} aria-label="Chart legend">
      {orientation === 'vertical' && (
        <li className="mb-1 min-h-[20px] text-[10px] font-semibold uppercase leading-[20px] tracking-[0.12em] text-slate-500">
          Legend
        </li>
      )}
      {sorted.map((e) => (
        <li key={e.name} className={itemClass}>
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: e.color }}
            aria-hidden="true"
          />
          <span
            className="min-w-0 flex-1 truncate font-medium leading-[20px] text-slate-700"
            title={e.name}
          >
            {e.name}
          </span>
          <span className="font-semibold leading-[20px] text-slate-900">
            {fmtMoney(e.value)}
          </span>
          {showPercent && total !== undefined && total > 0 && (
            <span className="leading-[20px] text-slate-500">{fmtPercent(e.value, total)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

// =============================================================================
// Pie label — only rendered for "big enough" slices to avoid overlap mess
// =============================================================================

interface PieLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
  value?: number;
  index?: number;
  name?: string;
}

const RADIAN = Math.PI / 180;
const MIN_SLICE_PCT_FOR_LABEL = 0.04; // hide labels on slices < 4%

function renderPieLabel(props: PieLabelProps): React.ReactElement | null {
  const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0, percent = 0, value = 0 } = props;
  if (percent < MIN_SLICE_PCT_FOR_LABEL) return null;
  // Position the label OUTSIDE the slice with a leader-line look.
  const radius = outerRadius * 1.12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  // Narrow charts (phone / narrow dashboard cards) get percent-only labels —
  // the full "$1.65M · 45%" string clips at the svg edge below ~280px of
  // plot width; the RichLegend still shows the full value per slice.
  const compact = cx < 140;
  return (
    <text
      x={x}
      y={y}
      fill="#334155"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
      className="tabular-nums"
    >
      {compact ? `${(percent * 100).toFixed(0)}%` : `${fmtMoney(value)} · ${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// =============================================================================
// ChartRenderer
// =============================================================================

export function ChartRenderer({ spec }: { spec: ChartSpec }) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 sm:px-4">
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800" title={spec.title}>
          {spec.title}
        </h3>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => containerRef.current && void exportChartAsPNG(containerRef.current, spec.title)}
            className="btn-ghost h-8 min-h-0 px-2 text-xs"
            aria-label="Export as PNG"
          >
            PNG
          </button>
          <button
            type="button"
            onClick={() => containerRef.current && void exportChartAsPDF(containerRef.current, spec.title)}
            className="btn-ghost h-8 min-h-0 px-2 text-xs"
            aria-label="Export as PDF"
          >
            PDF
          </button>
        </div>
      </div>
      <div ref={containerRef} className="bg-white p-4">
        {/* Mobile + tablet: container grows naturally so chart + legend don't
            collide with the summary_text below. The fixed 340px only applies
            at lg, together with the side-by-side chart/legend layout (below
            lg the app sidebar leaves too little width for two columns). */}
        <div className="min-h-[280px] w-full lg:h-[340px]">
          <ChartCore spec={spec} />
        </div>
        {spec.summary_text && (
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{spec.summary_text}</p>
        )}
      </div>
    </div>
  );
}

function ChartCore({ spec }: { spec: ChartSpec }) {
  const { chart_type, series } = spec;

  // ---- PIE ----------------------------------------------------------------
  if (chart_type === 'pie') {
    const data = (series[0]?.data ?? []).map((p, i) => ({
      name: String(p.x),
      value: Number(p.y),
      color: PALETTE[i % PALETTE.length]!,
    }));
    const total = data.reduce((s, p) => s + p.value, 0);

    return (
      // Side-by-side on desktop (lg = 1024px and up), stacked below that —
      // between 640-1023px the app sidebar (256px) leaves too little width
      // for pie + side legend. On the stacked layout the pie gets an explicit
      // fixed height (240px) so Recharts has something to render in, and the
      // legend takes its natural height below — the parent container's min-h
      // grows with the content rather than clipping it.
      // At lg+ both columns stretch to the parent's fixed 340px; the legend
      // width is proportional (capped) so narrow dashboard-grid cards don't
      // starve the pie column.
      <div className="flex flex-col gap-3 lg:h-full lg:flex-row lg:gap-5">
        <div className="h-[240px] w-full min-w-0 lg:h-full lg:min-h-[200px] lg:w-auto lg:flex-[1.5]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                outerRadius="78%"
                label={renderPieLabel}
                labelLine={false}
                isAnimationActive={false}
                stroke="#fff"
                strokeWidth={1.5}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip total={total} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-full lg:w-[38%] lg:min-w-[150px] lg:max-w-[260px] lg:shrink-0 lg:self-center lg:overflow-y-auto lg:max-h-full">
          <RichLegend
            entries={data.map((d) => ({ name: d.name, color: d.color, value: d.value }))}
            total={total}
            showPercent
            orientation="vertical"
          />
        </div>
      </div>
    );
  }

  // ---- Wide-format merge for bar / line / area / scatter / combo ----------
  const xs = Array.from(new Set(series.flatMap((s) => s.data.map((d) => String(d.x)))));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const merged: any[] = xs.map((x) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row: any = { x };
    for (const s of series) {
      const point = s.data.find((d) => String(d.x) === x);
      row[s.name] = point ? Number(point.y) : null;
    }
    return row;
  });

  // Per-series totals for the rich legend
  const seriesTotals = useMemo<LegendEntry[]>(
    () =>
      series.map((s, i) => ({
        name: s.name,
        color: s.color ?? PALETTE[i % PALETTE.length]!,
        value: s.data.reduce((sum, p) => sum + Number(p.y || 0), 0),
      })),
    [series],
  );

  // X-axis labels are rendered DIAGONALLY (-35°) by default so long category
  // names ("Investment Tax Credit (ITC)", "Utility Allowance Study", etc.)
  // don't overlap horizontally. Diagonal text gets more usable run-length
  // and reads cleanly from bottom-left to top-right.
  //
  // Truncation is relaxed accordingly — the rotated track has space for
  // ~22 characters before crowding the next tick. Tooltip + legend always
  // show the full name regardless of truncation.
  const xTickFormatter = (val: string): string => {
    const s = String(val);
    return s.length > 22 ? s.slice(0, 20) + '…' : s;
  };

  // Bottom margin reserves room for the rotated tick labels. Without this,
  // the labels collide with the chart's bottom edge or with the x-axis
  // label below them.
  const xAxisHeight = spec.x_axis_label ? 90 : 70;
  const chartMargin = { top: 8, right: 16, bottom: spec.x_axis_label ? 8 : 8, left: 8 };

  // Axis configuration shared by the rectangular chart types.
  const commonAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis
        dataKey="x"
        tick={{ fontSize: 11, fill: '#475569' }}
        tickFormatter={xTickFormatter}
        interval={0}
        height={xAxisHeight}
        angle={-35}
        textAnchor="end"
        tickMargin={4}
        label={
          spec.x_axis_label
            ? {
                value: spec.x_axis_label,
                position: 'insideBottom',
                offset: 0,
                fill: '#64748b',
                fontSize: 11,
              }
            : undefined
        }
      />
      <YAxis
        tick={{ fontSize: 11, fill: '#475569' }}
        tickFormatter={(v: number) => fmtMoney(v)}
        width={64}
        label={
          spec.y_axis_label
            ? {
                value: spec.y_axis_label,
                angle: -90,
                position: 'insideLeft',
                offset: 6,
                style: { textAnchor: 'middle', fill: '#64748b', fontSize: 11 },
              }
            : undefined
        }
      />
      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(15,23,42,0.04)' }} />
      {/* Default legend hidden — we render a richer one below the chart. */}
      <Legend content={() => null} />
    </>
  );

  // Wrap the chart + custom legend. Multi-series charts get a side legend on
  // desktop widths (lg+, matching the diligence-audience expectation,
  // mirroring Power BI / Looker), stacking below on mobile/tablet — under lg
  // the app sidebar leaves too little width for two columns. The legend width
  // is proportional (capped) so narrow dashboard-grid cards can't starve the
  // chart column to zero. Single-series charts skip the legend entirely
  // (it'd be redundant with the title).
  const wrapWithLegend = (chart: React.ReactNode): React.ReactElement => {
    // Below lg the outer container has no fixed height, and Recharts'
    // ResponsiveContainer (height:100%) collapses to 0 inside an auto-height
    // parent — min-height on the wrapper does NOT fix that. So the chart box
    // gets an EXPLICIT height below lg and only uses h-full at lg+ where the
    // outer 340px container is definite.
    if (series.length <= 1) {
      return (
        <div className="h-[280px] lg:h-full">
          <ResponsiveContainer>{chart as React.ReactElement}</ResponsiveContainer>
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col gap-3 lg:flex-row lg:gap-5">
        <div className="h-[260px] w-full min-w-0 lg:h-full lg:min-h-[200px] lg:w-auto lg:flex-[1.6]">
          <ResponsiveContainer width="100%" height="100%">
            {chart as React.ReactElement}
          </ResponsiveContainer>
        </div>
        <div className="w-full lg:w-[35%] lg:min-w-[140px] lg:max-w-[240px] lg:shrink-0 lg:self-center lg:overflow-y-auto lg:py-2">
          <RichLegend entries={seriesTotals} orientation="vertical" />
        </div>
      </div>
    );
  };

  switch (chart_type) {
    case 'line':
      return wrapWithLegend(
        <LineChart data={merged} margin={chartMargin}>
          {commonAxes}
          {series.map((s, i) => (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={s.color ?? PALETTE[i % PALETTE.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>,
      );
    case 'bar':
    case 'stacked_bar': {
      // Special case: single-series bar chart against categorical X (e.g.
      // "Open AR by Service Line"). Treat each X value as its own category,
      // color each bar individually from the palette, and render a side
      // legend identical to the pie chart's. This gives the diligence
      // audience visual consistency between pie + bar views of the same
      // breakdown.
      if (chart_type === 'bar' && series.length === 1) {
        const s = series[0]!;
        const categoryEntries: LegendEntry[] = xs.map((x, i) => {
          const point = s.data.find((d) => String(d.x) === x);
          return {
            name: x,
            color: PALETTE[i % PALETTE.length]!,
            value: Number(point?.y ?? 0),
          };
        });
        const total = categoryEntries.reduce((sum, e) => sum + e.value, 0);
        // Single-series categorical bar with side legend: hide the X-axis
        // tick labels — they'd duplicate the legend (every category appears
        // there with its color, $ value, %). The category-colored bars +
        // legend are enough; the bottom labels were causing diagonal-text
        // crowding for no informational gain.
        return (
          // Same explicit-height-below-lg rule as wrapWithLegend — see the
          // comment there (ResponsiveContainer collapses in auto-height parents).
          <div className="flex h-full flex-col gap-3 lg:flex-row lg:gap-5">
            <div className="h-[260px] w-full min-w-0 lg:h-full lg:min-h-[200px] lg:w-auto lg:flex-[1.6]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={merged} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="x"
                    tick={false}
                    tickLine={false}
                    axisLine={{ stroke: '#cbd5e1' }}
                    height={20}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#475569' }}
                    tickFormatter={(v: number) => fmtMoney(v)}
                    width={64}
                    label={
                      spec.y_axis_label
                        ? {
                            value: spec.y_axis_label,
                            angle: -90,
                            position: 'insideLeft',
                            offset: 6,
                            style: { textAnchor: 'middle', fill: '#64748b', fontSize: 11 },
                          }
                        : undefined
                    }
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(15,23,42,0.04)' }} />
                  <Bar dataKey={s.name} radius={[3, 3, 0, 0]}>
                    {merged.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full lg:w-[38%] lg:min-w-[150px] lg:max-w-[260px] lg:shrink-0 lg:self-center lg:overflow-y-auto lg:max-h-full lg:py-2">
              <RichLegend
                entries={categoryEntries}
                total={total}
                showPercent
                orientation="vertical"
              />
            </div>
          </div>
        );
      }
      // Multi-series (or stacked): use the standard wrapper with one legend
      // entry per series.
      return wrapWithLegend(
        <BarChart data={merged} margin={chartMargin}>
          {commonAxes}
          {series.map((s, i) => (
            <Bar
              key={s.name}
              dataKey={s.name}
              fill={s.color ?? PALETTE[i % PALETTE.length]}
              stackId={chart_type === 'stacked_bar' ? 'stack' : undefined}
              radius={[3, 3, 0, 0]}
            />
          ))}
        </BarChart>,
      );
    }
    case 'area':
      return wrapWithLegend(
        <AreaChart data={merged} margin={chartMargin}>
          {commonAxes}
          {series.map((s, i) => {
            const color = s.color ?? PALETTE[i % PALETTE.length]!;
            return (
              <Area
                key={s.name}
                type="monotone"
                dataKey={s.name}
                stroke={color}
                fill={color}
                fillOpacity={0.3}
                strokeWidth={2}
              />
            );
          })}
        </AreaChart>,
      );
    case 'scatter':
      return wrapWithLegend(
        <ScatterChart margin={chartMargin}>
          {commonAxes}
          {series.map((s, i) => (
            <Scatter
              key={s.name}
              name={s.name}
              data={s.data.map((d) => ({ x: d.x, y: d.y }))}
              fill={s.color ?? PALETTE[i % PALETTE.length]}
            />
          ))}
        </ScatterChart>,
      );
    case 'combo':
      return wrapWithLegend(
        <ComposedChart data={merged} margin={chartMargin}>
          {commonAxes}
          {series.map((s, i) => {
            const color = s.color ?? PALETTE[i % PALETTE.length]!;
            if (s.type === 'line')
              return <Line key={s.name} type="monotone" dataKey={s.name} stroke={color} strokeWidth={2} dot={false} />;
            if (s.type === 'area')
              return (
                <Area
                  key={s.name}
                  type="monotone"
                  dataKey={s.name}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              );
            return <Bar key={s.name} dataKey={s.name} fill={color} radius={[3, 3, 0, 0]} />;
          })}
        </ComposedChart>,
      );
  }
}
